/**
 * TUVI - Componente de Login
 * Pantalla de inicio de sesión con animaciones de construcción
 */

import React, { useState, useEffect } from 'react';
import './Login.css';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Animación de partículas de construcción
  useEffect(() => {
    const createParticle = () => {
      const container = document.querySelector('.login-particles');
      if (!container) return;

      const particle = document.createElement('div');
      particle.className = 'particle';

      // Tipos de partículas: ladrillos, herramientas, etc.
      const types = ['brick', 'gear', 'hammer', 'ruler', 'bolt'];
      const type = types[Math.floor(Math.random() * types.length)];
      particle.classList.add(type);

      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDuration = (Math.random() * 10 + 15) + 's';
      particle.style.animationDelay = Math.random() * 5 + 's';

      container.appendChild(particle);

      // Remover después de la animación
      setTimeout(() => {
        particle.remove();
      }, 25000);
    };

    // Crear partículas iniciales
    for (let i = 0; i < 15; i++) {
      setTimeout(createParticle, i * 200);
    }

    // Continuar creando partículas
    const interval = setInterval(createParticle, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setError('Por favor ingresa usuario y contraseña');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const gqlClient = (await import('../../services/graphqlClient')).default;
      const data = await gqlClient.mutate(`
        mutation Login($username: String!, $password: String!) {
          login(username: $username, password: $password) {
            token
            user { id username nombreCompleto rol activo permisos { modulo submodulo tieneAcceso } }
          }
        }
      `, { username, password });

      const { token: authToken, user: gqlUser } = data.login;

      // Mapear permisos a formato plano { "modulo.submodulo": true }.
      // Módulos sin subcategorías (submodulo vacío) usan solo el módulo como key.
      const permisos = {};
      (gqlUser.permisos || []).forEach(p => {
        const key = p.submodulo ? `${p.modulo}.${p.submodulo}` : p.modulo;
        permisos[key] = p.tieneAcceso;
      });

      const userData = {
        id: gqlUser.id,
        username: gqlUser.username,
        nombre_completo: gqlUser.nombreCompleto,
        rol: gqlUser.rol,
        activo: gqlUser.activo,
        permisos
      };

      localStorage.setItem('tuvi_token', authToken);
      localStorage.setItem('tuvi_user', JSON.stringify(userData));
      localStorage.setItem('tuvi_last_activity', Date.now().toString());

      onLogin(userData, authToken);

      // Success path - no else needed since errors throw
      setError(''); // clear any previous error
    } catch (err) {
      console.error('Login error:', err);
      setError('Error de conexión. Verifica que el servidor esté activo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Fondo animado */}
      <div className="login-background">
        <div className="grid-overlay"></div>
        <div className="blueprint-lines"></div>
        <div className="login-particles"></div>

        {/* Elementos de construcción animados */}
        <div className="construction-elements">
          <div className="crane crane-1">
            <div className="crane-tower"></div>
            <div className="crane-arm"></div>
            <div className="crane-cable"></div>
            <div className="crane-hook"></div>
          </div>
          <div className="building building-1"></div>
          <div className="building building-2"></div>
          <div className="building building-3"></div>
          <div className="scaffold scaffold-1"></div>
        </div>
      </div>

      {/* Contenido del login */}
      <div className="login-content">
        <div className="login-card">
          {/* Logo y título */}
          <div className="login-header">
            <div className="logo-container">
              <img src="/logo.png" alt="TUVI" className="login-logo" />
            </div>
            <h1>Bienvenido</h1>
            <p>Sistema de Gestión de Proyectos</p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Usuario"
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  Ingresando...
                </>
              ) : (
                <>
                  Ingresar
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p>TUVI v1.0 - Sistema de Gestión de Proyectos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
